<template>
  <Teleport to="#app">
    <div id="modal-container" class="modal-container">
      <component
        :is="modal.component"
        v-for="modal in modals"
        :key="modal.modalId"
        :params="modal.componentAttrs"
        v-on="modal.listeners"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import RandomUtils from "@utils/random.utils";
import { ref, watch, onMounted } from "vue";
import DOMUtils from "@utils/dom.utils";
import modalEventBus from "@plugins/modal/service/event-bus";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { ModalComponentAttrs, ModalEntry } from "@plugins/modal/types.ts";

/**
 * <modal-container/> is wrapper component for <content-modal/> to support dynamic modal.
 */

const modals = ref<ModalEntry[]>([]);
const { trackWatch } = useEventBusWatch();

function getComponentName(component: { name?: string; __name?: string }): string | undefined {
  return component.name ?? component.__name;
}

watch(
  modals,
  (modalList) => {
    if (modalList.length === 0) {
      DOMUtils.removeClass(document.documentElement, "modal-open");
      DOMUtils.removeClass("body", "bx--body--with-modal-open");
      DOMUtils.removeClass("body", "rich-text-modal-open");
    } else {
      DOMUtils.addClass(document.documentElement, "modal-open");
      modalList.forEach((entry) => {
        if (getComponentName(entry.component) === "RichTextModal") {
          DOMUtils.addClass("body", "rich-text-modal-open");
        }
      });
    }
  },
  { deep: true },
);

onMounted(() => {
  trackWatch(
    modalEventBus.$on("modal-hidden", (id: unknown) => {
      remove(String(id));
    }),
  );
});

function add(
  component,
  componentAttrs: ModalComponentAttrs = {},
  listeners = {},
  allowMultipleModals = false,
) {
  //check if there is existing modal
  const currentModal: ModalEntry = modals.value[0];
  if (modals.value.length > 0) {
    const componentName = getComponentName(component);
    const currentModalComponentName = getComponentName(currentModal.component);
    if (componentName === currentModalComponentName) {
      return;
    } else if (!allowMultipleModals) {
      modals.value.pop();
    }
  }
  const PREFIX = "modal_";
  const modalId = PREFIX + RandomUtils.randomId();

  componentAttrs.modalId = modalId;

  modals.value.push({
    modalId,
    component,
    componentAttrs,
    listeners,
  });

  return modalId;
}

function remove(modalId: string) {
  const index = modals.value.findIndex((entry) => entry.modalId === modalId);
  if (index !== -1) {
    modals.value.splice(index, 1);
  }
}

function removeAll() {
  modals.value = [];
}

defineExpose({
  add,
  remove,
  removeAll,
});
</script>
